<template>
  <page-wrapper title="รายงาน Error ไม่ส่งไป SAP (MM)">
    <div class="page-container">
      <div class="row" style="padding-left: 35px">
        <div class="query-box">
          <div class="label col-12">วันที่ในใบเสร็จรับเงิน</div>
          <date-picker
            v-model="receiptDateRange"
            type="daterange"
          ></date-picker>
        </div>
        <div class="query-box">
          <div class="label col-12">ผู้ให้บริการ</div>
          <provider-selector v-model="providerId"></provider-selector>
        </div>
      </div>
      <div class="text-end">
        <sc-button variant="primary" @click="handleClickSearch"
          >ค้นหา</sc-button
        >
      </div>
      <div class="report-container">
        <div class="csv">
          <button variant="secondary" @click.prevent="handleClickDownloadAsCSV">
            Download
          </button>
        </div>
        <div class="report">
          <div
            v-for="(mmReportError, index) in sapMmReportErrorPresenters"
            :key="index"
            :class="{ header: index === 0 }"
            class="mm-report-row"
          >
            <div class="report-cell">
              <div v-if="mmReportError.appointmentRoute">
                <router-link :to="mmReportError.appointmentRoute">{{
                  mmReportError.appointmentText
                }}</router-link>
              </div>
              <div v-else>{{ mmReportError.appointmentText }}</div>
            </div>
            <div class="report-cell">
              <div v-if="mmReportError.receiptRoute">
                <router-link :to="mmReportError.receiptRoute">{{
                  mmReportError.documentNumberText
                }}</router-link>
              </div>
              <div v-else>{{ mmReportError.documentNumberText }}</div>
            </div>
            <div class="report-cell">
              {{ mmReportError.providerName }}
            </div>
            <div class="report-cell">
              {{ mmReportError.receiptSignedAtDate }}
            </div>
            <div class="report-cell">
              {{ mmReportError.receiptType }}
            </div>
            <div class="report-cell">
              {{ mmReportError.customerName }}
            </div>
            <div class="report-cell">
              {{ mmReportError.errorMassage }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import DatePicker from "@/lib/components/DatePicker"
import { DateTime } from "luxon"
import PageWrapper from "@/lib/components/PageWrapper"
import { baanRueJaiClient } from "@/dependencies"
import ScButton from "@/lib/components/ScButton"
import { receiptTypeEN } from "@/lib/mapper"
import { existValueAndEmptyString } from "@/lib/util"
import ProviderSelector from "@/lib/components/ProviderSelector"

export default {
  components: {
    PageWrapper,
    DatePicker,
    ScButton,
    ProviderSelector,
  },
  data() {
    return {
      sapMmErrorReports: [],
      receiptDateRange: [],
      query: [],
      providerId: null,
    }
  },
  computed: {
    sapMmReportErrorPresenters() {
      const header = {
        appointmentText: "นัดหมาย",
        documentNumberText: "เลขที่ใบเสร็จรับเงิน/ใบกำกับภาษี",
        receiptSignedAtDate: "วันที่ใบเสร็จ",
        receiptType: "ประเภทใบเสร็จ/ใบกำกับภาษี",
        customerName: "ชื่อลูกค้า",
        errorMassage: "Error massage",
        providerName: "ชื่อผู้ให้บริการ",
      }

      return [header, ...this.sapMmErrorReports]
    },
    queryParams() {
      const { receiptDateFrom, receiptDateTo, providerId } = this.$route.query

      return {
        receiptDateFrom,
        receiptDateTo,
        providerId,
      }
    },
  },
  watch: {
    "$route.query": function routeQueryWatcher() {
      this.setupData()
    },
  },
  created() {
    this.setup()
  },
  methods: {
    setup() {
      this.setupForm()
      this.setupData()
    },
    setupForm() {
      const { receiptDateFrom, receiptDateTo, providerId } = this.$route.query

      this.providerId = providerId

      if (!receiptDateFrom && !receiptDateTo) {
        return
      }

      this.receiptDateRange = [
        DateTime.fromISO(receiptDateFrom),
        DateTime.fromISO(receiptDateTo),
      ]
    },
    setupData() {
      this.setupSapMmErrorReports()
    },
    async setupSapMmErrorReports() {
      const sapMmErrorReports = await this.fetchSapMmErrorReports()

      this.sapMmErrorReports = sapMmErrorReports.map((sapMmErrorReport) => {
        const appointmentRoute = sapMmErrorReport.appointment_id
          ? {
              name: "AppointmentDetailPage",
              params: { id: sapMmErrorReport.appointment_id },
            }
          : undefined

        const receiptRoute = existValueAndEmptyString(
          sapMmErrorReport.receipt_id,
        )
          ? {
              name: "ReceiptDetailPage",
              params: { id: sapMmErrorReport.receipt_id },
            }
          : undefined

        let documentNumberText = ""
        if (existValueAndEmptyString(sapMmErrorReport.document_number)) {
          documentNumberText = sapMmErrorReport.document_number
        } else if (existValueAndEmptyString(sapMmErrorReport.receipt_id)) {
          documentNumberText = "กดเพื่อดูใบเสร็จ"
        }

        return {
          appointmentRoute,
          receiptRoute,
          customerName: sapMmErrorReport.customer_name,
          errorMassage: sapMmErrorReport.error_massage,
          documentNumberText,
          receiptType:
            sapMmErrorReport.receipt_type === receiptTypeEN.TAX_INVOICE
              ? "ใบกำกับภาษี"
              : "ใบเสร็จรับเงิน/ใบลดหนี้",
          appointmentText: sapMmErrorReport.appointment_id
            ? "กดเพื่อดูนัดหมาย"
            : "",
          receiptSignedAtDate: sapMmErrorReport.signed_at,
          providerName: sapMmErrorReport.provider_name,
        }
      })
    },
    async fetchSapMmErrorReports() {
      const response = await baanRueJaiClient.admin.sap.getSapMmErrorReportList(
        this.queryParams,
      )

      return response.data.result
    },
    handleClickSearch() {
      let receiptDateFrom
      let receiptDateTo
      if (this.receiptDateRange && this.receiptDateRange.length > 1) {
        receiptDateFrom = this.receiptDateRange[0].toISO()
        receiptDateTo = this.receiptDateRange[1].toISO()
      }
      const providerId = this.providerId
        ? this.providerId.id || this.providerId
        : ""

      const query = {
        receiptDateFrom,
        receiptDateTo,
        providerId,
      }
      this.$router.push({ query })
    },
    async handleClickDownloadAsCSV(event) {
      event.preventDefault()
      const response = await baanRueJaiClient.admin.sap.getSapMmErrorReportList(
        {
          ...this.queryParams,
          format: "xlsx",
        },
      )

      const blob = new Blob([response.data])
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `sap-mm-error-reports.xlsx`
      link.click()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";
.report-container {
  padding: 20px 0;
  width: 100%;
}

.mm-report-row {
  display: grid;
  grid-template-columns:
    200px
    250px
    400px
    250px
    250px
    350px
    600px;
}

.mm-report-row:first-child {
  position: sticky;
  top: 0;

  .report-cell {
    border-top: 1px solid #444;
    font-weight: bold;
    background: #ddd;
  }
}

.report-cell {
  padding: 10px;
  border-left: 1px solid rgb(206, 204, 204);
  border-bottom: 1px solid rgb(228, 225, 225);
}

.report {
  padding: 15px;
}

.query-box {
  padding-right: 10px;
  padding-bottom: 10px;
  padding-top: 15px;
}

.csv {
  padding-left: 14px;
}
</style>
